import { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Typography } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { RecordManagement } from "app/class/record-management";
import SysColumn from "app/class/sys-column";
import { useTables } from "app/hooks/useTables";
import { useColumns } from "app/hooks/useColumns";
import ColumnOverrideForm, { OverrideValues, emptyOverrideValues } from "../components/ColumnOverrideForm";
import { extractOverrideErrors } from "../services/overrideErrors";
import { getSafeReturnToPath } from "modules/record/shared/services/recordNavigationReturn";
import queryKeys from "shared/services/queryKeys";

type OverrideRecord = OverrideValues & { id: string; sys_table_id: string; sys_column_id: string; active: boolean };

export default function ColumnOverridePage() {
    const { tableId, columnId } = useParams();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const location = useLocation();
    const { data: tables } = useTables();
    const { data: columns } = useColumns();
    const [column, setColumn] = useState<SysColumn | null>(null);
    const [override, setOverride] = useState<OverrideRecord | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof OverrideValues, string>>>({});
    const [confirmRevert, setConfirmRevert] = useState(false);
    const [reverting, setReverting] = useState(false);
    const [reverted, setReverted] = useState(false);
    const [formVersion, setFormVersion] = useState(0);

    useEffect(() => {
        let mounted = true;
        if (!tableId || !columnId) return;
        Promise.all([
            new RecordManagement<{ data: SysColumn }>("sys_columns").get(columnId),
            new RecordManagement<OverrideRecord>("sys_column_overrides")
                .where("sys_table_id", tableId).where("sys_column_id", columnId).paginate(1, 1).run(),
        ]).then(([source, records]) => {
            if (!mounted) return;
            setColumn(new SysColumn(source.data as any));
            setOverride(records.data[0] ?? null);
            setLoading(false);
        }).catch((reason) => {
            if (!mounted) return;
            setError(String(reason?.message || reason));
            setLoading(false);
        });
        return () => { mounted = false; };
    }, [tableId, columnId]);

    const invalidateOverrideMetadata = async () => {
        await Promise.all([
            queryKeys.records.lists("sys_columns"),
            queryKeys.records.all("sys_column_overrides"),
            queryKeys.metadata.columns,
            ["sys_column_overrides", "all"],
            queryKeys.metadata.tables,
            queryKeys.records.all("sys_tables"),
        ].map(queryKey => queryClient.invalidateQueries({ queryKey })));
    };

    const save = async (values: OverrideValues) => {
        if (!tableId || !columnId) return;
        setFieldErrors({});
        setError("");
        try {
            const repository = new RecordManagement<OverrideRecord>("sys_column_overrides");
            const normalized = { ...values, label: values.label?.trim() || null };
            const payload = { ...normalized, sys_table_id: tableId, sys_column_id: columnId,
                active: Object.values(normalized).some(value => value !== null && value !== "") };
            if (override) await repository.update(override.id, payload);
            else await repository.save(payload);
            await invalidateOverrideMetadata();
            const returnTo = getSafeReturnToPath(new URLSearchParams(location.search).get("return_to"));
            navigate(returnTo || `/home/workspace/sys_tables/record/${tableId}?related_list=inheritance_ancestors`);
        } catch (reason: any) {
            const errors = extractOverrideErrors(reason, key => String(t(key)));
            setFieldErrors(errors);
            if (!Object.keys(errors).length) setError(String(t(reason?.response?.data?.error?.message || "override.save_failed")));
        }
    };

    const revertAll = async () => {
        if (!tableId || !columnId || reverting) return;
        setReverting(true);
        setFieldErrors({});
        setError("");
        const cleared: OverrideValues = { ...emptyOverrideValues };
        try {
            const repository = new RecordManagement<OverrideRecord>("sys_column_overrides");
            if (override) await repository.update(override.id, { ...cleared, active: false, sys_table_id: tableId, sys_column_id: columnId });
            await invalidateOverrideMetadata();
            setOverride(current => current ? { ...current, ...cleared, active: false } : null);
            setFormVersion(version => version + 1);
            setConfirmRevert(false);
            setReverted(true);
        } catch (reason: any) {
            setConfirmRevert(false);
            setError(String(t(reason?.response?.data?.error?.message || "override.save_failed")));
        } finally { setReverting(false); }
    };

    if (loading) return <CircularProgress />;
    return <Box sx={{ maxWidth: 800, mx: "auto", p: { xs: 2, md: 3 }, minWidth: 0 }}>
        <Typography variant="h5" component="h1" gutterBottom sx={{ mb: 3, overflowWrap: "anywhere" }}>{column ? `${String(t(column.label))} ${String(t("override.in_table"))} ${String(t(tables.find(item => item.id === tableId)?.label || ""))}` : String(t("inheritance.column.customize"))}</Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {column && <ColumnOverrideForm key={`${override?.id || column.id}:${formVersion}`} inherited={column} initial={override || undefined} errors={fieldErrors} onSave={save} onRevertAll={() => setConfirmRevert(true)}
            table={tables.find(item => item.id === tableId)} tables={tables} effectiveColumns={columns.filter(item => item.sys_table_id === tableId)}
            referencedColumns={columns.filter(item => item.sys_table_id === tables.find(table => table.name === column.referenced_table_name)?.id)} />}
        <Dialog open={confirmRevert} onClose={() => { if (!reverting) setConfirmRevert(false); }} aria-labelledby="revert-all-title">
            <DialogTitle id="revert-all-title">{String(t("override.revert_all"))}</DialogTitle>
            <DialogContent>{String(t("override.revert_all_confirm"))}</DialogContent>
            <DialogActions>
                <Button onClick={() => setConfirmRevert(false)} disabled={reverting}>{String(t("override.cancel"))}</Button>
                <Button onClick={() => void revertAll()} disabled={reverting} variant="contained">{String(t("override.confirm"))}</Button>
            </DialogActions>
        </Dialog>
        <Snackbar open={reverted} autoHideDuration={4000} onClose={() => setReverted(false)}>
            <Alert severity="success" onClose={() => setReverted(false)}>{String(t("override.reverted_to_parent", { table: String(t(tables.find(item => item.id === column?.sys_table_id)?.label || "")) }))}</Alert>
        </Snackbar>
    </Box>;
}
